import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FiUser, FiHome, FiTruck, FiCheck, FiFileText, FiShield, FiGlobe, FiAlertCircle } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { useKYC } from '../../contexts/KYCContext';

const KYCContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 2rem;
  min-height: 100vh;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 1rem;
  font-weight: 700;
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: #a0a0a0;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const ProgressBar = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 3rem;
  gap: 2rem;
`;

const ProgressStep = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  
  .step-number {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    background: ${props => props.active ? 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' : 
                       props.completed ? '#00ff88' : 'rgba(255, 255, 255, 0.1)'};
    border: 2px solid ${props => props.active ? '#4facfe' : props.completed ? '#00ff88' : 'rgba(255, 255, 255, 0.2)'};
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${props => props.active || props.completed ? '#000' : '#fff'};
    font-weight: 700;
  }
  
  .step-label {
    font-size: 0.9rem;
    color: ${props => props.active ? '#4facfe' : props.completed ? '#00ff88' : '#a0a0a0'};
    text-align: center;
  }
`;

const StepContainer = styled.div`
  background: rgba(255, 255, 255, 0.02);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 3rem;
  margin-bottom: 2rem;
`;

const StepTitle = styled.h2`
  font-size: 1.8rem;
  color: #fff;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const StepDescription = styled.p`
  color: #a0a0a0;
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  color: #fff;
  margin-bottom: 0.5rem;
  font-weight: 600;
`;

const Input = styled.input`
  width: 100%;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  color: #fff;
  font-size: 1rem;
  
  &:focus {
    border-color: #4facfe;
    box-shadow: 0 0 0 3px rgba(79, 172, 254, 0.1);
  }
  
  &::placeholder {
    color: #a0a0a0;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  color: #fff;
  font-size: 1rem;
  
  &:focus {
    border-color: #4facfe;
    box-shadow: 0 0 0 3px rgba(79, 172, 254, 0.1);
  }
  
  option {
    background: #1a1a3e;
    color: #fff;
  }
`;

const UploadedFile = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  background: rgba(0, 255, 136, 0.1);
  border: 1px solid rgba(0, 255, 136, 0.3);
  border-radius: 10px;
  margin-bottom: 1rem;
  
  .file-info {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #00ff88;
  }
`;

const RequirementsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 1.5rem 0;
  
  li {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0;
    color: #a0a0a0;
    
    .icon {
      color: #4facfe;
    }
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
`;

const Button = styled.button`
  padding: 1rem 2rem;
  border-radius: 10px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &.primary {
    background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
    color: #000;
    border: none;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 10px 30px rgba(79, 172, 254, 0.3);
    }
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none;
    }
  }
  
  &.secondary {
    background: transparent;
    color: #4facfe;
    border: 1px solid #4facfe;
    
    &:hover {
      background: rgba(79, 172, 254, 0.1);
    }
  }
`;

const AlertBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.5rem;
  background: rgba(255, 165, 0, 0.1);
  border: 1px solid rgba(255, 165, 0, 0.3);
  border-radius: 10px;
  margin-bottom: 2rem;
  
  .icon {
    color: #ffa500;
    margin-top: 0.2rem;
  }
  
  .content {
    color: #fff;
    line-height: 1.6;
    
    h3 {
      margin-bottom: 0.5rem;
      color: #ffa500;
    }
  }
`;

const CompletionCard = styled.div`
  text-align: center;
  padding: 3rem;
  background: rgba(0, 255, 136, 0.1);
  border: 1px solid rgba(0, 255, 136, 0.3);
  border-radius: 20px;
  
  .success-icon {
    font-size: 4rem;
    color: #00ff88;
    margin-bottom: 1.5rem;
  }
  
  h2 {
    color: #00ff88;
    margin-bottom: 1rem;
    font-size: 2rem;
  }
  
  p {
    color: #a0a0a0;
    margin-bottom: 2rem;
    line-height: 1.6;
  }
`;

function KYCVerification() {
  const navigate = useNavigate();
  const { kycStatus, updateKYCData, updateKYCStatus } = useKYC();
  const [currentStep, setCurrentStep] = useState(kycStatus.step);
  const [formData, setFormData] = useState(kycStatus.data);

  const steps = [
    { number: 1, label: 'Identity Verification', icon: FiUser },
    { number: 2, label: 'Address Verification', icon: FiHome },
    { number: 3, label: 'Delivery Requirements', icon: FiTruck }
  ];

  const handleInputChange = (field, value) => {
    const stepMap = {
      firstName: 'identity', lastName: 'identity', dateOfBirth: 'identity', 
      nationality: 'identity', idType: 'identity', idNumber: 'identity',
      idDocument: 'identity', selfieDocument: 'identity',
      
      streetAddress: 'address', city: 'address', state: 'address',
      postalCode: 'address', country: 'address', addressDocument: 'address',
      
      deliveryType: 'sugarDelivery', businessType: 'sugarDelivery', 
      sugarUsage: 'sugarDelivery', minimumQuantity: 'sugarDelivery',
      preferredWarehouse: 'sugarDelivery', importLicense: 'sugarDelivery',
      storageCapability: 'sugarDelivery', qualityRequirements: 'sugarDelivery'
    };
    
    const stepName = stepMap[field];
    if (stepName) {
      updateKYCData(stepName, { [field]: value });
    }
    
    setFormData(prev => ({
      ...prev,
      [stepName]: {
        ...prev[stepName],
        [field]: value
      }
    }));
  };

  const handleNextStep = () => {
    if (validateCurrentStep()) {
      const newStep = currentStep + 1;
      setCurrentStep(newStep);
      updateKYCStatus({ 
        step: newStep, 
        status: newStep === 4 ? 'under_review' : 'in_progress' 
      });
      toast.success('Step completed successfully');
    }
  };

  const handlePrevStep = () => {
    const newStep = currentStep - 1;
    setCurrentStep(newStep);
    updateKYCStatus({ step: newStep, status: 'in_progress' });
  };

  const validateCurrentStep = () => {
    switch (currentStep) {
      case 1:
        const identity = formData.identity || {};
        return identity.firstName && identity.lastName && identity.dateOfBirth && 
               identity.nationality && identity.idType && identity.idNumber;
      case 2:
        const address = formData.address || {};
        return address.streetAddress && address.city && address.country;
      case 3:
        const sugar = formData.sugarDelivery || {};
        return sugar.deliveryType && sugar.businessType && sugar.sugarUsage;
      default:
        return true;
    }
  };

  const handleSubmit = () => {
    updateKYCStatus({ 
      status: 'under_review',
      step: 4,
      submittedAt: new Date().toISOString()
    });
    toast.success('KYC verification submitted successfully! Review typically takes 1-2 business days.');
    setCurrentStep(4);
  };

  const handleStartTrading = () => {
    updateKYCStatus('verified');
    toast.success('Your KYC verification is complete. You can continue in the Core Tokenizer.');
    navigate('/tokenization');
  };

  const renderStep1 = () => (
    <StepContainer>
      <StepTitle>
        <FiUser />
        Identity Verification
      </StepTitle>
      <StepDescription>
        Please provide your personal identification details. All information is encrypted and securely stored.
      </StepDescription>

      <FormGrid>
        <FormGroup>
          <Label>First Name *</Label>
          <Input
            type="text"
            value={formData.identity?.firstName}
            onChange={(e) => handleInputChange('firstName', e.target.value)}
            placeholder="Enter your first name"
          />
        </FormGroup>

        <FormGroup>
          <Label>Last Name *</Label>
          <Input
            type="text"
            value={formData.identity?.lastName}
            onChange={(e) => handleInputChange('lastName', e.target.value)}
            placeholder="Enter your last name"
          />
        </FormGroup>

        <FormGroup>
          <Label>Date of Birth *</Label>
          <Input
            type="date"
            value={formData.identity?.dateOfBirth}
            onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
          />
        </FormGroup>

        <FormGroup>
          <Label>Nationality *</Label>
          <Select
            value={formData.identity?.nationality}
            onChange={(e) => handleInputChange('nationality', e.target.value)}
          >
            <option value="">Select nationality</option>
            <option value="US">United States</option>
            <option value="UK">United Kingdom</option>
            <option value="DE">Germany</option>
            <option value="FR">France</option>
            <option value="SG">Singapore</option>
            <option value="AE">United Arab Emirates</option>
            <option value="CH">Switzerland</option>
            <option value="other">Other</option>
          </Select>
        </FormGroup>

        <FormGroup>
          <Label>ID Document Type *</Label>
          <Select
            value={formData.identity?.idType}
            onChange={(e) => handleInputChange('idType', e.target.value)}
          >
            <option value="">Select ID type</option>
            <option value="passport">Passport</option>
            <option value="national_id">National ID Card</option>
            <option value="driving_license">Driving License</option>
          </Select>
        </FormGroup>

        <FormGroup>
          <Label>ID Number *</Label>
          <Input
            type="text"
            value={formData.identity?.idNumber}
            onChange={(e) => handleInputChange('idNumber', e.target.value)}
            placeholder="Enter ID number"
          />
        </FormGroup>
      </FormGrid>

      <FormGroup>
        <Label>Upload ID Document *</Label>
        {formData.identity?.idDocument && (
          <UploadedFile>
            <div className="file-info">
              <FiCheck />
              <span>{formData.identity.idDocument.name}</span>
            </div>
          </UploadedFile>
        )}
      </FormGroup>

      <RequirementsList>
        <li><FiFileText className="icon" /> High-quality, full document visible</li>
        <li><FiFileText className="icon" /> All text clearly readable</li>
        <li><FiFileText className="icon" /> Document must be current and valid</li>
      </RequirementsList>
    </StepContainer>
  );

  const renderStep2 = () => (
    <StepContainer>
      <StepTitle>
        <FiHome />
        Address Verification
      </StepTitle>
      <StepDescription>
        Verify your residential address for delivery and compliance purposes.
      </StepDescription>

      <FormGrid>
        <FormGroup>
          <Label>Street Address *</Label>
          <Input
            type="text"
            value={formData.address?.streetAddress}
            onChange={(e) => handleInputChange('streetAddress', e.target.value)}
            placeholder="Enter your street address"
          />
        </FormGroup>

        <FormGroup>
          <Label>City *</Label>
          <Input
            type="text"
            value={formData.address?.city}
            onChange={(e) => handleInputChange('city', e.target.value)}
            placeholder="Enter your city"
          />
        </FormGroup>

        <FormGroup>
          <Label>State/Province</Label>
          <Input
            type="text"
            value={formData.address?.state}
            onChange={(e) => handleInputChange('state', e.target.value)}
            placeholder="Enter your state/province"
          />
        </FormGroup>

        <FormGroup>
          <Label>Postal Code</Label>
          <Input
            type="text"
            value={formData.address?.postalCode}
            onChange={(e) => handleInputChange('postalCode', e.target.value)}
            placeholder="Enter postal code"
          />
        </FormGroup>

        <FormGroup>
          <Label>Country *</Label>
          <Select
            value={formData.address?.country}
            onChange={(e) => handleInputChange('country', e.target.value)}
          >
            <option value="">Select country</option>
            <option value="US">United States</option>
            <option value="UK">United Kingdom</option>
            <option value="DE">Germany</option>
            <option value="FR">France</option>
            <option value="SG">Singapore</option>
            <option value="AE">United Arab Emirates</option>
            <option value="CH">Switzerland</option>
          </Select>
        </FormGroup>
      </FormGrid>

      <FormGroup>
        <Label>Proof of Address Document *</Label>
        {formData.address?.addressDocument && (
          <UploadedFile>
            <div className="file-info">
              <FiCheck />
              <span>{formData.address.addressDocument.name}</span>
            </div>
          </UploadedFile>
        )}
      </FormGroup>

      <RequirementsList>
        <li><FiFileText className="icon" /> Document dated within last 3 months</li>
        <li><FiFileText className="icon" /> Name and address clearly visible</li>
        <li><FiFileText className="icon" /> Official letterhead or stamps visible</li>
      </RequirementsList>
    </StepContainer>
  );

  const renderStep3 = () => (
    <StepContainer>
      <StepTitle>
        <FiTruck />
        Sugar Delivery Requirements
      </StepTitle>
      <StepDescription>
        Configure your sugar delivery preferences and provide necessary documentation for physical redemption.
      </StepDescription>

      <AlertBox>
        <FiAlertCircle className="icon" />
        <div className="content">
          <h3>Important: Physical Sugar Delivery</h3>
          <p>
            Physical delivery of sugar requires additional compliance checks and may require import licenses 
            depending on your location and quantity. Minimum delivery quantities and fees apply.
          </p>
        </div>
      </AlertBox>

      <FormGrid>
        <FormGroup>
          <Label>Delivery Type *</Label>
          <Select
            value={formData.sugarDelivery?.deliveryType}
            onChange={(e) => handleInputChange('deliveryType', e.target.value)}
          >
            <option value="">Select delivery type</option>
            <option value="warehouse_pickup">Warehouse Pickup</option>
            <option value="local_delivery">Local Delivery</option>
            <option value="international_shipping">International Shipping</option>
          </Select>
        </FormGroup>

        <FormGroup>
          <Label>Business Type *</Label>
          <Select
            value={formData.sugarDelivery?.businessType}
            onChange={(e) => handleInputChange('businessType', e.target.value)}
          >
            <option value="">Select business type</option>
            <option value="individual">Individual Investor</option>
            <option value="food_manufacturer">Food Manufacturer</option>
            <option value="trading_company">Trading Company</option>
            <option value="retailer">Retailer</option>
            <option value="restaurant_chain">Restaurant Chain</option>
            <option value="other">Other</option>
          </Select>
        </FormGroup>

        <FormGroup>
          <Label>Sugar Usage Purpose *</Label>
          <Select
            value={formData.sugarDelivery?.sugarUsage}
            onChange={(e) => handleInputChange('sugarUsage', e.target.value)}
          >
            <option value="">Select usage purpose</option>
            <option value="investment_storage">Investment/Storage</option>
            <option value="food_production">Food Production</option>
            <option value="beverage_production">Beverage Production</option>
            <option value="resale">Resale</option>
            <option value="personal_use">Personal Use</option>
          </Select>
        </FormGroup>

        <FormGroup>
          <Label>Minimum Delivery Quantity (kg) *</Label>
          <Select
            value={formData.sugarDelivery?.minimumQuantity}
            onChange={(e) => handleInputChange('minimumQuantity', e.target.value)}
          >
            <option value="">Select minimum quantity</option>
            <option value="100">100 kg (2 bags)</option>
            <option value="500">500 kg (10 bags)</option>
            <option value="1000">1,000 kg (1 MT pallet)</option>
            <option value="5000">5,000 kg (5 MT)</option>
            <option value="10000">10,000 kg (10 MT)</option>
          </Select>
        </FormGroup>

        <FormGroup>
          <Label>Preferred Warehouse Location</Label>
          <Select
            value={formData.sugarDelivery?.preferredWarehouse}
            onChange={(e) => handleInputChange('preferredWarehouse', e.target.value)}
          >
            <option value="">No preference</option>
            <option value="dubai">Dubai DMCC Warehouse</option>
            <option value="singapore">Singapore Port Storage</option>
            <option value="switzerland">Swiss Commodity Vault</option>
          </Select>
        </FormGroup>

        <FormGroup>
          <Label>Quality Requirements</Label>
          <Select
            value={formData.sugarDelivery?.qualityRequirements}
            onChange={(e) => handleInputChange('qualityRequirements', e.target.value)}
          >
            <option value="">No specific requirements</option>
            <option value="icumsa_45">ICUMSA 45 (Standard White)</option>
            <option value="icumsa_30">ICUMSA 30 (Premium White)</option>
            <option value="organic">Organic Certified</option>
            <option value="fair_trade">Fair Trade Certified</option>
          </Select>
        </FormGroup>
      </FormGrid>

      {formData.sugarDelivery?.businessType === 'food_manufacturer' && (
        <FormGroup>
          <Label>Import License (for international delivery)</Label>
          {formData.sugarDelivery?.importLicense && (
            <UploadedFile>
              <div className="file-info">
                <FiCheck />
                <span>{formData.sugarDelivery.importLicense.name}</span>
              </div>
            </UploadedFile>
          )}
        </FormGroup>
      )}

      <RequirementsList>
        <li><FiTruck className="icon" /> Delivery fees calculated based on location and quantity</li>
        <li><FiShield className="icon" /> All deliveries are fully insured during transit</li>
        <li><FiGlobe className="icon" /> International deliveries may require 7-14 business days</li>
        <li><FiFileText className="icon" /> Import documentation may be required for international shipments</li>
      </RequirementsList>
    </StepContainer>
  );

  const renderCompletion = () => (
    <CompletionCard>
      <FiCheck className="success-icon" />
      <h2>KYC Verification Complete!</h2>
      <p>
        Your verification has been submitted successfully. Our compliance team will review your 
        documents within 24-48 hours. You'll receive an email notification once approved.
      </p>
      <p>
        <strong>Next Steps:</strong><br />
        • Check your email for verification updates<br />
        • You can now proceed to trade sugar tokens<br />
        • Physical delivery will be enabled after final approval
      </p>
      <Button className="primary" onClick={handleStartTrading}>
        Start Trading Sugar Tokens
      </Button>
    </CompletionCard>
  );

  return (
    <KYCContainer>
      <Header>
        <Title>Sugar Token KYC Verification</Title>
        <Subtitle>
          Complete identity verification to unlock physical sugar delivery and full trading capabilities
        </Subtitle>
      </Header>

      <ProgressBar>
        {steps.map((step) => (
          <ProgressStep
            key={step.number}
            active={currentStep === step.number}
            completed={currentStep > step.number}
          >
            <div className="step-number">
              {currentStep > step.number ? <FiCheck /> : step.number}
            </div>
            <div className="step-label">{step.label}</div>
          </ProgressStep>
        ))}
      </ProgressBar>

      {currentStep === 1 && renderStep1()}
      {currentStep === 2 && renderStep2()}
      {currentStep === 3 && renderStep3()}
      {currentStep === 4 && renderCompletion()}

      {currentStep < 4 && (
        <ButtonGroup>
          {currentStep > 1 && (
            <Button className="secondary" onClick={handlePrevStep}>
              Previous Step
            </Button>
          )}
          {currentStep < 3 ? (
            <Button
              className="primary"
              onClick={handleNextStep}
              disabled={!validateCurrentStep()}
            >
              Next Step
            </Button>
          ) : (
            <Button
              className="primary"
              onClick={handleSubmit}
              disabled={!validateCurrentStep()}
            >
              Submit Verification
            </Button>
          )}
        </ButtonGroup>
      )}
    </KYCContainer>
  );
}

export default KYCVerification; 